import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { useTheme } from '../hooks/useTheme'
import toast from 'react-hot-toast'
import logo from '../assets/logo-full-2.svg'

const Login: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  const { login, isAuthenticated, error, clearError } = useAuthStore()
  const { theme } = useTheme()
  const navigate = useNavigate()
  
  // Determine logo source - use theme logo if available, otherwise default
  // Theme logos are now local assets, so they're already imported/bundled
  const logoSource = theme?.logo?.url || logo
  const logoAlt = theme?.tenant?.name || 'Zanders Group'

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/')
    }
  }, [isAuthenticated, navigate])

  useEffect(() => {
    if (error) {
      toast.error(error)
      clearError()
    }
  }, [error, clearError])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email || !password) {
      toast.error('Please fill in all fields')
      return
    }

    setIsSubmitting(true)
    
    try {
      const success = await login(email, password)
      if (success) {
        toast.success('Login successful!')
        navigate('/')
      }
    } catch (error) {
      toast.error('Login failed. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="flex justify-center mb-6">
            <img 
              src={logoSource} 
              alt={logoAlt} 
              className="h-12 w-auto"
              style={{ filter: theme?.logo ? 'none' : 'brightness(0) saturate(100%)' }}
              onError={(e) => {
                // Fallback to default logo if theme logo fails to load
                if (theme?.logo) {
                  (e.target as HTMLImageElement).src = logo
                }
              }}
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {theme?.tenant?.name || 'Zanders Group'} Support Platform
          </p>
        </div>
        
        <div className="card">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="input-field mt-1"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="input-field mt-1"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            
            <div>
              <button
                type="submit"
                className="btn-primary w-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
          
          {/* DEMO CREDENTIALS NOTICE - COMMENTED OUT FOR PRODUCTION
          <div className="mt-6 text-center text-sm text-gray-600">
            <p className="font-medium text-gray-700 mb-2">Demo Credentials:</p>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-gray-500">Admin User:</p>
                <p className="font-mono text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded">
                  admin@zandersgrp.com / admin123
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Client User:</p>
                <p className="font-mono text-xs text-gray-700 bg-gray-100 px-2 py-1 rounded">
                  client@sampleclient.com / client123
                </p>
              </div>
            </div>
          </div>
          */}
        </div>
      </div>
    </div>
  )
}

export default Login
